import React, { useCallback, useState, useEffect } from 'react';
import { Table, Row, Col, Select } from 'antd';
import { getTestResultFiles } from '../../../lib/api/aws-api';
import { getDataset, getDatasetVersionById } from '../../../lib/api/dataset-api';
import { useHistory, useParams } from 'react-router-dom';
import { getDatasetOverviewTableColumns, getDatasetOverviewTableComparingColumns } from './ScoreDatasetOverviewTable';
import { PageHeader } from '../../../components/layout/PageHeader';
import { TestResultRow, Pagination, AlgorithmVersion } from '../../../types';
import { Dataset, DatasetVersion } from '../../../types/dataset';
import { getAlgorithmValue } from '../../../lib/util/algorithm';
import { useGetAlgorithmAndVersions } from '../../../lib/hooks';
import { getAlgorithmVersionById } from '../../../lib/api/algorithm-api';

const { Option } = Select;

const defaultPaging = {
  pageSize: 10,
  page: 1,
  showSizeChanger: false,
};

const ScoreDatasetOverview = () => {
  let history = useHistory();

  const { datasetVersionId, algorithmVersionId } = useParams() as {
    datasetVersionId: string;
    algorithmVersionId: string;
  };

  const [paginationObject] = useState<Pagination>(defaultPaging);
  const [paginationObject2] = useState<Pagination>(defaultPaging);
  const [files, setFiles] = useState<TestResultRow[]>([]);
  const [comparingFiles, setComparingFiles] = useState<any[]>([]);

  const [dataset, setDataset] = useState<Dataset>();
  const [datasetVersion, setDatasetVersion] = useState<DatasetVersion>();
  const [loading, setLoading] = useState<boolean>(false);

  const [comparedAlgorithmVersions, setComparedAlgorithmVersions] = useState<AlgorithmVersion[]>([]);

  const { algorithm, algorithmVersions, selectedAlgorithmVersion } = useGetAlgorithmAndVersions(algorithmVersionId);

  useEffect(() => {
    const fetch = async () => {
      if (selectedAlgorithmVersion && comparedAlgorithmVersions.length) {
        setLoading(true);
        const algorithmVersions = [selectedAlgorithmVersion, ...comparedAlgorithmVersions].sort((a, b) =>
          a?.timeCreated! > b?.timeCreated! ? 1 : -1
        );
        let allFiles: any[] = [];
        await Promise.all(
          algorithmVersions.map(async algoVersion => {
            const algorithmVersion = await getAlgorithmVersionById(algoVersion.id);
            const newFiles = await getTestResultFiles(algorithmVersion.id, datasetVersion?.version_id || '');
            allFiles = [...allFiles, ...newFiles.map(newFile => ({ ...newFile, version: algoVersion.id }))];
          })
        );
        const files = allFiles.map((file, i, array) => {
          let object: any = { file_id: file.file_id };
          algorithmVersions.forEach(algoVersion => {
            if (array.find(f => f.version === algoVersion.id && f.file_id === file.file_id)) {
              object[('version_' + algoVersion.id) as keyof {}] = array.find(
                f => f.version === algoVersion.id && f.file_id === file.file_id
              ).incorrect_fields;
            } else {
              object[('version_' + algoVersion.id) as keyof {}] = null;
            }
          });
          return object;
        });
        const uniqueIds: string[] = [];
        const filteredOutDoubleIds = files
          .filter(file => {
            const isDuplicate = uniqueIds.includes(file.file_id);
            if (!isDuplicate) {
              uniqueIds.push(file.file_id);
              return true;
            }
            return false;
          })
          .sort((a, b) => a.file_id.localeCompare(b.file_id));
        setComparingFiles(filteredOutDoubleIds);
        setLoading(false);
      } else {
        setComparingFiles([]);
      }
    };
    fetch();
  }, [comparedAlgorithmVersions, datasetVersion, files, selectedAlgorithmVersion]);

  const fetchFiles = useCallback(
    async (datasetVersionId, { page, pageSize } = defaultPaging, algorithmVersion, filter = null) => {
      const datasetVersion = await getDatasetVersionById(datasetVersionId);
      const dataset_ = await getDataset(datasetVersion.dataset_id);
      setDataset(dataset_);
      setDatasetVersion(datasetVersion);
      const files = await getTestResultFiles(algorithmVersion.id, datasetVersion.version_id);
      const sortedFiles = files.sort((a, b) => a.file_id.localeCompare(b.file_id));
      setFiles(sortedFiles);
      setLoading(false);
    },
    [setFiles]
  );

  useEffect(() => {
    setLoading(true);
    if (datasetVersionId && selectedAlgorithmVersion) {
      fetchFiles(datasetVersionId, paginationObject, selectedAlgorithmVersion);
    }
  }, [datasetVersionId, fetchFiles, paginationObject, selectedAlgorithmVersion]);

  const onClickUrl = useCallback(
    (record: any, algoVersion: string | undefined = algorithmVersionId) => {
      return `/overview/${algoVersion}/datasets/${datasetVersion?.version_id}/dataset-file/${record.file_id}`;
    },
    [algorithmVersionId, datasetVersion]
  );

  return (
    <>
      <PageHeader
        back={{ url: '/overview', label: 'Overview' }}
        header={`${algorithm ? getAlgorithmValue(algorithm.name) : ''}: ${dataset ? dataset.name : ''}`}
        description="Click on a file to see the annotated fields."
      />
      <Row style={{ margin: '36px 0 20px 0' }}>
        <Col span={24}>
          <span>Select a version: </span>
          <Select
            value={selectedAlgorithmVersion?.version}
            style={{ width: '150px' }}
            onChange={(value: any) => {
              const algorithmVersion = algorithmVersions.find(a => a.version === value)!;
              history.push(`/overview/${algorithmVersion.id}/datasets/${datasetVersion?.version_id}`);
            }}>
            {algorithmVersions
              .filter(av => (comparedAlgorithmVersions.length ? !comparedAlgorithmVersions.includes(av) : av))
              .map(algorithmVersion => (
                <Option key={algorithmVersion.version} value={algorithmVersion.version}>
                  {algorithmVersion.version}
                </Option>
              ))}
          </Select>
          <span className="ml-4">Compare with: </span>
          <Select
            mode="multiple"
            allowClear
            value={comparedAlgorithmVersions?.map(algoVersion => algoVersion.version)}
            style={{ width: '250px' }}
            onChange={(value: any) => {
              const algoVersions = algorithmVersions.filter(a => value.includes(a.version))!;
              setComparedAlgorithmVersions(algoVersions);
            }}
            onClear={() => {
              setComparedAlgorithmVersions([]);
            }}>
            {algorithmVersions
              .filter(av => av.id !== selectedAlgorithmVersion?.id)
              .map(algorithmVersion => (
                <Option key={algorithmVersion.version} value={algorithmVersion.version}>
                  {algorithmVersion.version}
                </Option>
              ))}
          </Select>
        </Col>
        <Col span={12} style={{ display: 'flex', justifyContent: 'flex-end' }}></Col>
      </Row>
      <Row>
        <Col span={24}>
          {selectedAlgorithmVersion && comparedAlgorithmVersions.length ? (
            <Table
              className="ant-table-toolbox"
              columns={getDatasetOverviewTableComparingColumns(onClickUrl, [
                selectedAlgorithmVersion,
                ...comparedAlgorithmVersions,
              ])}
              rowKey={record => record.file_id}
              dataSource={comparingFiles}
              loading={loading || comparingFiles.length === 0}
              pagination={paginationObject}
            />
          ) : null}
          {!comparedAlgorithmVersions.length ? (
            <Table
              className="ant-table-toolbox"
              columns={getDatasetOverviewTableColumns(onClickUrl)}
              rowKey={record => record.file_id}
              dataSource={files}
              loading={loading || files.length === 0}
              pagination={paginationObject2}
            />
          ) : null}
        </Col>
      </Row>
    </>
  );
};

export default ScoreDatasetOverview;
